// import components
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import FlexRequestHistoryTable from './FlexRequestHistoryTable';
import KeyValueDisplay from './KeyValueDisplay';
import NoteList from './NoteList';
import NotesBar from './NotesBar';

// import external libraries
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import useGetRequestNotes from '../hooks/useGetRequestNotes';

// import utils
import { calculateTimeDifference } from '../utils/calculateTimeDifference';
import { StyledBoxFlexColumnNoGap } from 'components/shared/Box/Box.styles';
import {
  StyledDialogBox,
  StyledDialogContentBox,
  StyledTopContentBox
} from './DetailsDialog.styles';

export const DetailsDialog = ({ open, onClose, row }) => {
  const { t } = useTranslation();
  const requestId = row?.request?.requestId;
  const processInstanceId = row?.processInstanceId;
  const requestDate = dayjs(row?.request?.date).format('DD-MM-YYYY');
  const costCenter = row?.request?.costCenter?.name;
  const functionDescription = row?.request?.function?.description;
  const dutyCode = row?.request?.duty?.code;
  const employeeName = row?.dutyLine?.employee?.nameCompound;

  const dutyStartTime = row?.request?.duty?.dutyStartTime
    ? dayjs(row?.request?.duty?.dutyStartTime)
    : null;
  const dutyEndTime = row?.request?.duty?.dutyEndTime
    ? dayjs(row?.request?.duty?.dutyEndTime)
    : null;
  const formattedDutyStartTime = dayjs(dutyStartTime).format('HH:mm');
  const formattedDutyEndTime = dayjs(dutyEndTime).format('HH:mm');

  const totalTimeFormatted = calculateTimeDifference(
    dutyStartTime,
    dutyEndTime
  );

  const { data: notes, isFetching } = useGetRequestNotes({ requestId });
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showTaskHistory, setShowTaskHistory] = useState(true);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 800,
            maxWidth: 800
          }
        }
      }}
    >
      <StyledDialogBox className={classNames('info')}>
        <DialogTitle id="confirmation-dialog">
          {t('flexHistory.flexRequestHistoryNr', { requestId })}
        </DialogTitle>
        <DialogContent>
          <StyledDialogContentBox>
            <StyledTopContentBox>
              <StyledBoxFlexColumnNoGap>
                {employeeName && (
                  <KeyValueDisplay label={t('employee')} value={employeeName} />
                )}
                <KeyValueDisplay label={t('date')} value={requestDate} />
                <KeyValueDisplay label={t('planUnit')} value={costCenter} />
                <KeyValueDisplay
                  label={t('function')}
                  value={functionDescription}
                />
              </StyledBoxFlexColumnNoGap>

              <StyledBoxFlexColumnNoGap>
                <KeyValueDisplay label={t('dutyCode')} value={dutyCode} />
                <KeyValueDisplay
                  label={t('period')}
                  value={
                    formattedDutyStartTime && formattedDutyEndTime
                      ? `${formattedDutyStartTime} - ${formattedDutyEndTime}`
                      : null
                  }
                />
                <KeyValueDisplay
                  label={t('flexHistory.totalTime')}
                  value={totalTimeFormatted}
                />
              </StyledBoxFlexColumnNoGap>
            </StyledTopContentBox>

            <FlexRequestHistoryTable
              processInstanceId={processInstanceId}
              setShowTaskHistory={setShowTaskHistory}
              showTaskHistory={showTaskHistory}
            />

            <NotesBar
              requestId={requestId}
              showNotes={showNotes}
              setShowNotes={setShowNotes}
              showNoteForm={showNoteForm}
              setShowNoteForm={setShowNoteForm}
              notes={notes}
            />

            {showNotes && <NoteList notes={notes} isFetching={isFetching} />}
          </StyledDialogContentBox>

          <DialogActions>
            <Button onClick={onClose} color="primary" variant="contained">
              {t('cancel')}
            </Button>
          </DialogActions>
        </DialogContent>
      </StyledDialogBox>
    </Dialog>
  );
};
